import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import http from '../http';
import { loginPathFor } from '../loginRedirect';
import logo from '../assets/logo.png';

// Leaves the app for the MCP client's redirect URI (a hosted callback, a
// loopback callback, workbuddy://...). Kept as a function so tests can observe it.
export const browser = {
  assign(url) {
    window.location.assign(url);
  }
};

function errorMessage(error) {
  if (error?.response?.status === 404) return '授权请求已过期，请回到 AI 助手重新连接 PicSect。';
  return '暂时无法处理授权请求，请稍后重试。';
}

export default function OAuthConsentPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const requestId = new URLSearchParams(location.search).get('request');
  const [request, setRequest] = useState(null);
  const [error, setError] = useState(requestId ? '' : '缺少授权请求参数。');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!requestId) return undefined;
    const here = `${location.pathname}${location.search}`;
    if (!localStorage.getItem('token')) {
      navigate(loginPathFor(here, { method: 'wechat' }), { replace: true });
      return undefined;
    }

    let active = true;
    http.get(`/user/oauth/requests/${encodeURIComponent(requestId)}`).then(response => {
      if (active) setRequest(response.data);
    }).catch(err => {
      if (!active) return;
      if ([401, 403].includes(err?.response?.status) && err.response.data?.code !== 'USER_BANNED') {
        navigate(loginPathFor(here, { method: 'wechat' }), { replace: true });
        return;
      }
      setError(err?.response?.data?.code === 'USER_BANNED' ? '账号已被封禁，无法授权。' : errorMessage(err));
    });
    return () => { active = false; };
  }, [requestId, location.pathname, location.search, navigate]);

  const decide = async decision => {
    setSubmitting(true);
    try {
      const response = await http.post(`/user/oauth/requests/${encodeURIComponent(requestId)}/${decision}`);
      browser.assign(response.data.redirectUrl);
    } catch (err) {
      setError(errorMessage(err));
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#EDE6D5', px: 2 }}>
      <Paper elevation={0} sx={{ width: '100%', maxWidth: 440, p: { xs: 3, sm: 4 }, borderRadius: 3 }}>
        <Stack spacing={2.5}>
          <Box component="img" src={logo} alt="PicSect" sx={{ height: 36, alignSelf: 'flex-start' }} />
          {error && <Alert severity="error" data-testid="oauth-consent-error">{error}</Alert>}
          {!error && !request && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
          )}
          {!error && request && (
            <>
              <Typography variant="h6" component="h1" data-testid="oauth-consent-title">
                {request.clientName} 想要连接你的 PicSect 账号
              </Typography>
              <Typography variant="body2" color="text.secondary">
                授权后，{request.clientName} 可以代表你：
              </Typography>
              <Stack spacing={1} component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
                {request.permissions.map(permission => (
                  <Stack key={permission} component="li" direction="row" spacing={1} alignItems="center">
                    <CheckCircleOutlineIcon fontSize="small" color="success" />
                    <Typography variant="body2">{permission}</Typography>
                  </Stack>
                ))}
              </Stack>
              <Typography variant="caption" color="text.secondary">
                不包括提现、修改密码、删除订单等操作。授权完成后将返回 {request.redirectHost}。
                可以随时在登录设备中移出该连接。
              </Typography>
              <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                <Button onClick={() => decide('deny')} disabled={submitting} data-testid="oauth-consent-deny">
                  拒绝
                </Button>
                <Button variant="contained" onClick={() => decide('approve')} disabled={submitting} data-testid="oauth-consent-approve">
                  授权
                </Button>
              </Stack>
            </>
          )}
        </Stack>
      </Paper>
    </Box>
  );
}
